/**
 * 層2: サーバー自動保存と送信キュー。docs/02-architecture.md §5。
 *
 * 保存ボタンという概念を持たない。ユーザーの注意力を保存の成否の監視に使わせない。
 *
 * このクラスが守る規律:
 *  1. 入力のたびにまずドラフトバッファへ書く（層1）。サーバーより先に、必ず。
 *  2. サーバー送信は最終変更から 3 秒の debounce。
 *  3. **発火時に composition 中なら送信しない。** 変換途中の文字列を確定本文として
 *     送らない（CLAUDE.md 絶対規則1）。`notifyCompositionEnd()` で再開する。
 *  4. 失敗しても本文を消さない。指数バックオフで自動再試行し続ける。
 *  5. 保存成功前にドラフトを消さない。
 *
 * React に依存しない素のクラスにしてあるのは、エディタの再レンダリングと
 * 保存の進行を完全に切り離すため（再描画は composition を壊す）。
 */

import { api, ApiError } from '@app/api/client';
import {
  deleteDraft,
  deleteDraftIfUnchanged,
  getDraft,
  isLocalNoteId,
  markDraftSynced,
  noteToDraftContent,
  promoteDraftAfterCreate,
  putDraft,
  stageDraft,
  type DraftCreateAttempt,
  type DraftRecord,
} from '@app/storage/draft-buffer';
import { SESSION_ID } from '@app/storage/session-id';
import {
  acquireDraftOwnership,
  type DraftOwnershipLease,
} from '@app/save/recovery-ownership';
import type { JstDate, Note } from '@shared/types';
import { shouldCreateNewNote } from '@shared/save-protocol';

/** 控えめに表示する保存状態（docs/01-requirements.md §5.9）。 */
export type SaveStatus = 'saved' | 'saving' | 'pending' | 'error';

export interface NoteContent {
  title: string | null;
  body: string;
  category_id: number | null;
  subject_date: JstDate;
  subject_date_end: JstDate | null;
}

export interface NoteSaverEvents {
  onStatusChange?: (status: SaveStatus) => void;
  /** 新規ノートの初回 POST が成功し、サーバー ID が確定したとき。 */
  onNoteIdAssigned?: (serverId: string, note: Note) => void;
  /** 古い版に基づく保存だった。控えめなトーストを出す（競合ダイアログは出さない）。 */
  onRegression?: () => void;
  /** セッション切れ。再ログインが必要。 */
  onUnauthorized?: () => void;
  /** ゴミ箱内のノートを編集しようとした（409）。復元を促す。 */
  onTrashedConflict?: () => void;
}

/** ドラフト書き込みの debounce。入力の手触りを損ねない範囲で最短にする。 */
const DRAFT_DEBOUNCE_MS = 300;
/** サーバー自動保存の debounce。 */
const SERVER_DEBOUNCE_MS = 3000;
const BACKOFF_BASE_MS = 1000;
const BACKOFF_MAX_MS = 60_000;

export class NoteSaver {
  #noteId: string;
  #baseVersion: number;
  #content: NoteContent;
  /** 直近サーバーへ送信して成功した本文。dirty 判定に使う。 */
  #syncedContent: NoteContent | null;
  /** 初回 POST の不変 payload。ID 移行成功までは現在内容が変わっても保持する。 */
  #createAttempt: DraftCreateAttempt | null = null;
  #createAttemptPersisted = false;
  /** stage/put/CAS へ同じ saved_at を渡すための直近 snapshot。 */
  #lastDraftSnapshot: DraftRecord | null = null;
  #events: NoteSaverEvents;
  #isComposing: () => boolean;

  #draftTimer: ReturnType<typeof setTimeout> | null = null;
  #serverTimer: ReturnType<typeof setTimeout> | null = null;
  #inFlight = false;
  /** 送信中にさらに入力があったか。完了後にもう一度送るために立てる。 */
  #resendAfterFlight = false;
  #failureCount = 0;
  #status: SaveStatus = 'saved';
  #disposed = false;
  #disposeRequested = false;
  #parkOnDispose = false;
  #abandoned = false;
  /** 回収スイープと同じドラフトを送らないための所有権（ADR-041）。 */
  #draftOwnership: DraftOwnershipLease;
  /** POST 応答後、local→server ID 移行が完了するまで両方を保護する。 */
  #serverOwnership: DraftOwnershipLease | null = null;
  #pendingDraftMigration: { localDraft: DraftRecord; serverId: string; version: number } | null =
    null;
  /** dispose() が送信中の POST/PUT を待ってから所有権を解放するための完了通知。 */
  #inFlightDone: Promise<void> | null = null;

  constructor(options: {
    noteId: string;
    baseVersion: number;
    content: NoteContent;
    /** 既にサーバーと一致している内容。新規ノートなら null。 */
    syncedContent: NoteContent | null;
    isComposing: () => boolean;
    events?: NoteSaverEvents;
    /** 既存ノートの非同期 open 開始時から保持していた lease を切れ目なく引き継ぐ。 */
    ownership?: DraftOwnershipLease;
  }) {
    this.#noteId = options.noteId;
    this.#baseVersion = options.baseVersion;
    this.#content = { ...options.content };
    this.#syncedContent = options.syncedContent ? { ...options.syncedContent } : null;
    this.#isComposing = options.isComposing;
    this.#events = options.events ?? {};
    this.#draftOwnership = options.ownership ?? acquireDraftOwnership(options.noteId);
  }

  get noteId(): string {
    return this.#noteId;
  }

  get status(): SaveStatus {
    return this.#status;
  }

  get baseVersion(): number {
    return this.#baseVersion;
  }

  get content(): NoteContent {
    return { ...this.#content };
  }

  get isDirty(): boolean {
    return !this.#syncedContent || !sameContent(this.#content, this.#syncedContent);
  }

  #setStatus(next: SaveStatus): void {
    if (this.#status === next) return;
    this.#status = next;
    this.#events.onStatusChange?.(next);
  }

  #draftRecord(): DraftRecord {
    return {
      note_id: this.#noteId,
      ...this.#content,
      base_version: this.#baseVersion,
      session_id: SESSION_ID,
      saved_at: new Date().toISOString(),
      dirty: this.isDirty,
      local_only: isLocalNoteId(this.#noteId),
      ...(this.#createAttempt ? { create_attempt: this.#createAttempt } : {}),
    };
  }

  #stageCurrentDraft(): DraftRecord {
    const draft = this.#draftRecord();
    this.#lastDraftSnapshot = draft;
    stageDraft(draft);
    return draft;
  }

  async #persistCurrentDraft(): Promise<DraftRecord> {
    const draft = this.#draftRecord();
    this.#lastDraftSnapshot = draft;
    const persisted = await putDraft(draft);
    this.#lastDraftSnapshot = persisted;
    return persisted;
  }

  /** 入力を受け取る。エディタからは composition 中でも呼んでよい。 */
  update(patch: Partial<NoteContent>): void {
    if (this.#disposed) return;
    this.#content = { ...this.#content, ...patch };
    // 送信中に入力があったことを記録する。通常は #syncedContent との差分（isDirty）で
    // 検出できるが、それだけに頼ると差分判定の変更で入力を静かに取りこぼし得る。
    // 絶対規則2に直結するので、事実として「飛行中に入力があった」も併せて持つ。
    if (this.#inFlight) this.#resendAfterFlight = true;
    if (this.isDirty) this.#setStatus('pending');

    // IndexedDB の 300ms debounce より前に画面を離れても、同じ SPA の閲覧画面が
    // 最新入力を読めるよう同期的な短命キャッシュへ置く。永続化は従来どおり下で行う。
    this.#stageCurrentDraft();

    // 層1: ドラフトは短い debounce で確実に書く。ネットワークに依存しない。
    if (this.#draftTimer) clearTimeout(this.#draftTimer);
    this.#draftTimer = setTimeout(() => {
      this.#draftTimer = null;
      void this.#persistCurrentDraft().catch(() => {
        // IndexedDB が使えない環境でも編集は継続させる。層2 が本文を運ぶ。
      });
    }, DRAFT_DEBOUNCE_MS);

    this.#scheduleServerSave(SERVER_DEBOUNCE_MS);
  }

  #scheduleServerSave(delayMs: number): void {
    if (this.#serverTimer) clearTimeout(this.#serverTimer);
    this.#serverTimer = setTimeout(() => {
      this.#serverTimer = null;
      void this.#trySend();
    }, delayMs);
  }

  /**
   * composition が終わったことをエディタから通知してもらう。
   * 変換中に送信を見送っていた場合、ここで速やかに再開する。
   */
  notifyCompositionEnd(): void {
    if (this.#disposed) return;
    if (this.isDirty && !this.#inFlight) this.#scheduleServerSave(0);
  }

  /**
   * 画面離脱・ノートを閉じる・タブが隠れるときに呼ぶ。debounce を待たずに送る。
   * 変換中なら送らない（規律を離脱時にも曲げない）。本文はドラフトに残っているため
   * 送れなくても失われない。
   */
  async flush(): Promise<void> {
    if (this.#disposed) return;
    if (this.#draftTimer) {
      clearTimeout(this.#draftTimer);
      this.#draftTimer = null;
    }
    // IndexedDB待機中にin-flight失敗のバックオフが積まれ得るため、既存timerだけを先に消す。
    // await後に消すと、新しく積まれた再試行まで誤って取り消してしまう。
    if (this.#serverTimer) {
      clearTimeout(this.#serverTimer);
      this.#serverTimer = null;
    }
    // 離脱時はドラフトを即座に確定させる（debounce の取りこぼしを防ぐ）。
    await this.#persistCurrentDraft().catch(() => undefined);
    // IndexedDB待機中にin-flightが失敗して新しいバックオフを積んだ場合は、
    // flushでその待機を飛び越えて即時再送しない。
    if (this.#serverTimer) return;
    await this.#trySend();
  }

  async #trySend(): Promise<void> {
    if (this.#disposed || this.#inFlight) return;
    if (this.#pendingDraftMigration) {
      await this.#retryDraftMigration();
      return;
    }
    if (!this.isDirty) {
      if (this.#disposeRequested) this.#completeDispose(!this.#parkOnDispose);
      else this.#setStatus('saved');
      return;
    }
    if (
      isLocalNoteId(this.#noteId) &&
      !this.#createAttempt &&
      !shouldCreateNewNote(this.#content.title, this.#content.body)
    ) {
      // metadata-only の新規ノートはローカル退避だけ行い、POST と再試行を始めない。
      this.#setStatus('pending');
      return;
    }
    // 絶対規則: 変換途中の文字列を確定本文として送らない。
    // ここで送らずに戻っても、notifyCompositionEnd() が再開させる。
    if (this.#isComposing()) {
      this.#setStatus('pending');
      return;
    }

    this.#inFlight = true;
    let finishFlight!: () => void;
    this.#inFlightDone = new Promise<void>((resolve) => {
      finishFlight = resolve;
    });
    this.#resendAfterFlight = false;
    this.#setStatus('saving');
    const attempted = { ...this.#content };

    try {
      let synced: NoteContent;
      if (isLocalNoteId(this.#noteId)) {
        synced = await this.#createOnServer(attempted);
      } else {
        synced = await this.#updateOnServer(attempted);
      }
      if (this.#abandoned) return;
      this.#failureCount = 0;
      this.#syncedContent = synced;
      await markDraftSynced(this.#noteId, this.#baseVersion, synced).catch(
        () => undefined,
      );
      // 送信中の追加入力を含めた現在値で、短命キャッシュの dirty 状態を確定する。
      this.#stageCurrentDraft();
      this.#inFlight = false;
      if (this.isDirty || this.#resendAfterFlight) {
        this.#setStatus('pending');
        this.#scheduleServerSave(0);
      } else if (this.#disposeRequested) {
        this.#completeDispose(!this.#parkOnDispose);
      } else {
        this.#setStatus('saved');
      }
    } catch (error) {
      this.#inFlight = false;
      this.#handleFailure(error);
    } finally {
      this.#inFlightDone = null;
      finishFlight();
    }
  }

  async #createOnServer(attempted: NoteContent): Promise<NoteContent> {
    const localId = this.#noteId;
    if (!this.#createAttempt) {
      this.#createAttempt = {
        body: attempted.body,
        title: attempted.title,
        category_id: attempted.category_id,
        subject_date: attempted.subject_date,
        subject_date_end: attempted.subject_date_end,
        session_id: SESSION_ID,
        client_request_id: localId,
      };
    }
    if (!this.#createAttemptPersisted) {
      // marker を失った状態で POST だけ成功すると応答喪失後に元 payload を再現できない。
      const persisted = await this.#persistCurrentDraft();
      if (!persisted.create_attempt) {
        throw new Error('作成開始payloadをドラフトへ永続化できませんでした');
      }
      // 別タブが先に永続化済みなら、stored側の先行markerを不変の送信元にする。
      this.#createAttempt = persisted.create_attempt;
      this.#createAttemptPersisted = true;
    }
    const note = await api.createNote(this.#createAttempt);
    const serverContent = noteToDraftContent(note);
    if (this.#abandoned) return serverContent;
    // 冪等再送では、attempted ではなく最初の POST で実際に作成済みの内容が返り得る。
    // この実サーバー内容を同期基準にし、応答喪失後の追加入力を dirty のまま残す。
    const localDraft: DraftRecord = {
      note_id: localId,
      ...serverContent,
      base_version: note.version,
      session_id: SESSION_ID,
      saved_at: new Date().toISOString(),
      dirty: true,
      local_only: true,
      create_attempt: this.#createAttempt,
    };
    this.#noteId = note.id;
    this.#baseVersion = note.version;
    this.#syncedContent = serverContent;
    // local と server を先に同時保有する。移行失敗時も local を回収側へ渡さず、次回は
    // POST ではなくこの移行だけを再試行する。プロセス喪失時は POST の冪等キーが守る。
    this.#serverOwnership = acquireDraftOwnership(note.id);
    this.#pendingDraftMigration = { localDraft, serverId: note.id, version: note.version };
    this.#events.onNoteIdAssigned?.(note.id, note);
    await this.#finishDraftMigration();
    return serverContent;
  }

  async #finishDraftMigration(): Promise<void> {
    const pending = this.#pendingDraftMigration;
    if (!pending) return;
    const promoted = await promoteDraftAfterCreate(
      pending.localDraft,
      pending.serverId,
      pending.version,
    );
    if (!promoted) throw new Error('ドラフト ID の移行を完了できませんでした');
    this.#draftOwnership.release();
    this.#draftOwnership = this.#serverOwnership!;
    this.#serverOwnership = null;
    this.#pendingDraftMigration = null;
    this.#createAttempt = null;
    this.#createAttemptPersisted = false;
  }

  async #retryDraftMigration(): Promise<void> {
    this.#inFlight = true;
    let finishFlight!: () => void;
    this.#inFlightDone = new Promise<void>((resolve) => {
      finishFlight = resolve;
    });
    try {
      await this.#finishDraftMigration();
      this.#failureCount = 0;
      if (this.#disposeRequested && this.#parkOnDispose) {
        this.#completeDispose(false);
      } else if (this.isDirty) {
        this.#setStatus('pending');
        if (!this.#isComposing()) this.#scheduleServerSave(0);
      } else if (this.#disposeRequested) {
        this.#completeDispose(true);
      } else {
        this.#setStatus('saved');
      }
    } catch (error) {
      this.#handleFailure(error);
    } finally {
      this.#inFlight = false;
      this.#inFlightDone = null;
      finishFlight();
    }
  }

  async #updateOnServer(attempted: NoteContent): Promise<NoteContent> {
    const result = await api.updateNote(this.#noteId, {
      body: attempted.body,
      title: attempted.title,
      category_id: attempted.category_id,
      subject_date: attempted.subject_date,
      subject_date_end: attempted.subject_date_end,
      base_version: this.#baseVersion,
      session_id: SESSION_ID,
    });
    this.#baseVersion = result.note.version;
    if (result.regression) this.#events.onRegression?.();
    // PUT はこの request 本文を原子的に書く契約。冪等 POST のように「過去の作成結果」を
    // 返す経路はないため、送信した内容を同期済み基準とする。
    return attempted;
  }

  #handleFailure(error: unknown): void {
    if (this.#disposed) return;

    if (error instanceof ApiError) {
      if (error.isUnauthorized) {
        // 再試行しても通らない。再ログインを促し、ドラフトは残したままにする。
        this.#setStatus('error');
        this.#events.onUnauthorized?.();
        return;
      }
      if (error.code === 'conflict') {
        this.#setStatus('error');
        this.#events.onTrashedConflict?.();
        return;
      }
      if (!error.retryable) {
        // バリデーション等。再送しても同じ結果になるので止める。本文はドラフトに残る。
        this.#setStatus('error');
        return;
      }
    }

    this.#failureCount++;
    this.#setStatus('error');
    const delay = Math.min(BACKOFF_BASE_MS * 2 ** (this.#failureCount - 1), BACKOFF_MAX_MS);
    this.#scheduleServerSave(delay);
  }

  /** 「今すぐ再試行」操作。バックオフを待たずに送る。 */
  retryNow(): void {
    if (this.#disposed) return;
    this.#failureCount = 0;
    this.#scheduleServerSave(0);
  }

  /**
   * このノートの編集を終える。保留中の送信を試みてからタイマーを止める。
   * ドラフトは消さない（次に開いたときの復元判定は decideDraftRestore が行う）。
   */
  async dispose(): Promise<void> {
    if (this.#disposed) return;
    this.#disposeRequested = true;
    const composingAtDispose = this.#isComposing();
    this.#parkOnDispose ||= composingAtDispose && this.isDirty;
    await this.flush().catch(() => undefined);
    // flush() 呼び出し時点ですでに送信中なら #trySend() は相乗りせず戻る。進行中の送信が
    // 終わるまでは回収へ所有権を渡さない（ここを待たないと同じ POST が二重になる）。
    const inFlight = this.#inFlightDone;
    if (inFlight) await inFlight;
    if (this.#disposed) return;
    // POST は成功したが ID 移行が未完了なら、所有権と再試行を維持する。通常の作成を
    // 再試行せず、#retryDraftMigration() が local→server 移行だけを完了させる。
    if (this.#pendingDraftMigration) return;
    // flushのIndexedDB待機とin-flight失敗が交差してtimerが観測できない場合も、
    // retryable失敗（failureCount > 0）を回収側へ渡さず自前再試行へ戻す。
    if (this.#failureCount > 0 && this.isDirty && !this.#serverTimer) {
      const delay = Math.min(
        BACKOFF_BASE_MS * 2 ** (this.#failureCount - 1),
        BACKOFF_MAX_MS,
      );
      this.#scheduleServerSave(delay);
    }
    // 追加入力または一時的な失敗の再試行タイマーがある間は saver が所有し続ける。
    // 一覧のスイープが一度見送った後でも、外部イベントなしで自前再送できる。
    if (this.#serverTimer || this.#inFlight) return;
    if (
      isLocalNoteId(this.#noteId) &&
      !this.#createAttempt &&
      !shouldCreateNewNote(this.#content.title, this.#content.body)
    ) {
      await this.#disposeEmptyLocalDraft();
      return;
    }
    // composition 中の離脱では回収側にも送らせない。module lease はページ再読込で消え、
    // その時点では変換セッションが終了しているので通常の起動回収へ戻る。
    this.#completeDispose(!this.#parkOnDispose);
  }

  /** marker のない空 local draft だけを、最大2回のCASで片付ける。 */
  async #disposeEmptyLocalDraft(): Promise<void> {
    const snapshot = this.#lastDraftSnapshot;
    if (snapshot && (await deleteDraftIfUnchanged(snapshot).catch(() => false))) {
      this.#completeDispose(true);
      return;
    }
    if (!this.#draftOwnership.isExclusive()) {
      this.#completeDispose(true);
      return;
    }

    // CAS競合後の再読込は1回だけ。新しい内容を古い #content で上書きしない。
    const current = await getDraft(this.#noteId).catch(() => undefined);
    if (!this.#draftOwnership.isExclusive()) {
      this.#completeDispose(true);
      return;
    }
    if (!current) {
      this.#completeDispose(true);
      return;
    }
    this.#content = {
      title: current.title,
      body: current.body,
      category_id: current.category_id,
      subject_date: current.subject_date,
      subject_date_end: current.subject_date_end,
    };
    this.#baseVersion = current.base_version;
    this.#lastDraftSnapshot = current;
    this.#createAttempt = current.create_attempt ?? null;
    this.#createAttemptPersisted = Boolean(current.create_attempt);

    if (this.#createAttempt || shouldCreateNewNote(current.title, current.body)) {
      await this.#trySend();
      if (this.#disposed || this.#pendingDraftMigration || this.#serverTimer || this.#inFlight) {
        return;
      }
      this.#completeDispose(!this.#parkOnDispose);
      return;
    }

    // 2回目も競合したら無条件削除せず、次の owner / startup sweep へ残す。
    await deleteDraftIfUnchanged(current).catch(() => false);
    this.#completeDispose(true);
  }

  #completeDispose(releaseOwnership: boolean): void {
    this.#disposed = true;
    if (this.#draftTimer) clearTimeout(this.#draftTimer);
    if (this.#serverTimer) clearTimeout(this.#serverTimer);
    this.#draftTimer = null;
    this.#serverTimer = null;
    if (releaseOwnership) {
      this.#draftOwnership.release();
      this.#serverOwnership?.release();
    } else {
      this.#draftOwnership.parkUntilNextOwner();
      this.#serverOwnership?.parkUntilNextOwner();
    }
  }

  /**
   * **保存せずに**終える。
   *
   * サーバー側でこのノートが書き換えられた直後（切り出しなど）に使う。この時点で
   * `#content` はサーバー側の変更を知らない古い内容なので、`dispose()` のように
   * flush してしまうとサーバーの変更を巻き戻す。呼び出し側は、この後に必ず
   * サーバーから読み直すこと。
   */
  abandon(): void {
    this.#abandoned = true;
    this.#disposed = true;
    if (this.#draftTimer) clearTimeout(this.#draftTimer);
    if (this.#serverTimer) clearTimeout(this.#serverTimer);
    this.#draftTimer = null;
    this.#serverTimer = null;
    const releaseOwnership = () => {
      this.#draftOwnership.release();
      this.#serverOwnership?.release();
    };
    // 応答処理が古いドラフトを復活させないよう、飛行中は所有権も維持する。
    if (this.#inFlightDone) void this.#inFlightDone.then(releaseOwnership);
    else releaseOwnership();
  }

  /** ノートを完全に破棄したとき（削除など）にドラフトも片付ける。 */
  async discardDraft(): Promise<void> {
    await deleteDraft(this.#noteId).catch(() => undefined);
  }
}

function sameContent(a: NoteContent, b: NoteContent): boolean {
  return (
    a.body === b.body &&
    (a.title ?? null) === (b.title ?? null) &&
    a.category_id === b.category_id &&
    a.subject_date === b.subject_date &&
    (a.subject_date_end ?? null) === (b.subject_date_end ?? null)
  );
}
